import { useCallback } from 'react';
import { Edit2 } from 'lucide-react';
import type { PhotoAnnotation, PixelCoordinate } from '../../types';

interface AnnotationWithId extends PhotoAnnotation {
  id?: string;
}

interface AnnotationLayerProps {
  annotations: AnnotationWithId[];
  pendingPoints: PixelCoordinate[];
  scale: number;
  annotationMode: 'point' | 'rectangle' | 'polygon' | null;
  onPointClick?: (point: PixelCoordinate, index: number) => void;
  onAnomalyClick?: (anomalyId: string) => void;
  rectStartPoint?: { x: number; y: number } | null;
  rectEndPoint?: { x: number; y: number } | null;
}

// Colors for different annotations
const ANNOTATION_COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
];

export function AnnotationLayer({ 
  annotations, 
  pendingPoints, 
  scale, 
  annotationMode,
  onPointClick,
  onAnomalyClick,
  rectStartPoint,
  rectEndPoint,
}: AnnotationLayerProps) {
  
  const getColor = useCallback((index: number) => {
    return ANNOTATION_COLORS[index % ANNOTATION_COLORS.length];
  }, []);

  const renderPoint = (point: PixelCoordinate, index: number, color: string, isPending = false) => {
    const size = isPending ? 24 : 20;
    const offset = size / 2;
    
    return (
      <div
        key={`point-${index}`}
        className={`absolute flex items-center justify-center cursor-pointer transition-transform hover:scale-110 ${
          isPending ? 'animate-pulse' : ''
        }`}
        style={{
          left: point.x * scale - offset,
          top: point.y * scale - offset,
          width: size,
          height: size,
        }}
        onClick={(e) => {
          e.stopPropagation();
          onPointClick?.(point, index);
        }}
      >
        <div 
          className="w-full h-full rounded-full border-2 border-white shadow-lg flex items-center justify-center text-white text-xs font-bold"
          style={{ backgroundColor: color }}
        >
          {index + 1}
        </div>
      </div>
    );
  };

  const renderPolygon = (points: PixelCoordinate[], color: string, isPending = false) => {
    if (points.length < 2) return null;

    // Create SVG path
    const pathData = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x * scale} ${p.y * scale}`)
      .join(' ') + (isPending ? '' : ' Z');

    return (
      <svg 
        className="absolute top-0 left-0 pointer-events-none" 
        style={{ 
          width: '100%', 
          height: '100%',
          overflow: 'visible'
        }}
      >
        {/* Fill */}
        {!isPending && points.length >= 3 && (
          <path
            d={pathData}
            fill={color}
            fillOpacity={0.2}
            stroke="none"
          />
        )}
        
        {/* Stroke */}
        <path
          d={pathData}
          fill="none"
          stroke={color}
          strokeWidth={isPending ? 2 : 3}
          strokeDasharray={isPending ? "5,5" : "none"}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        
        {/* Vertices */}
        {points.map((point, i) => (
          <circle
            key={i}
            cx={point.x * scale}
            cy={point.y * scale}
            r={isPending ? 4 : 6}
            fill={color}
            stroke="white"
            strokeWidth={2}
          />
        ))}
      </svg>
    );
  };

  const renderRectanglePreview = () => {
    if (!rectStartPoint) return null;
    
    const endX = rectEndPoint?.x ?? rectStartPoint.x;
    const endY = rectEndPoint?.y ?? rectStartPoint.y;
    
    const x = Math.min(rectStartPoint.x, endX) * scale;
    const y = Math.min(rectStartPoint.y, endY) * scale;
    const width = Math.abs(endX - rectStartPoint.x) * scale;
    const height = Math.abs(endY - rectStartPoint.y) * scale;
    
    return (
      <svg 
        className="absolute top-0 left-0 pointer-events-none" 
        style={{ 
          width: '100%', 
          height: '100%',
          overflow: 'visible'
        }}
      >
        {/* Rectangle fill */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="#f59e0b"
          fillOpacity={0.15}
        />
        
        {/* Rectangle stroke */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="none"
          stroke="#f59e0b"
          strokeWidth={2}
          strokeDasharray="5,5"
        />
        
        {/* Corner points */}
        <circle cx={rectStartPoint.x * scale} cy={rectStartPoint.y * scale} r={5} fill="#f59e0b" stroke="white" strokeWidth={2} />
        {rectEndPoint && (
          <>
            <circle cx={rectEndPoint.x * scale} cy={rectStartPoint.y * scale} r={4} fill="#f59e0b" stroke="white" strokeWidth={2} />
            <circle cx={rectEndPoint.x * scale} cy={rectEndPoint.y * scale} r={5} fill="#f59e0b" stroke="white" strokeWidth={2} />
            <circle cx={rectStartPoint.x * scale} cy={rectEndPoint.y * scale} r={4} fill="#f59e0b" stroke="white" strokeWidth={2} />
          </>
        )}
      </svg>
    );
  };

  // Calculate center point of annotation for edit button placement
  const getAnnotationCenter = (annotation: AnnotationWithId) => {
    if (annotation.points.length === 0) return { x: 0, y: 0 };
    const sumX = annotation.points.reduce((sum, p) => sum + p.x, 0);
    const sumY = annotation.points.reduce((sum, p) => sum + p.y, 0);
    return {
      x: sumX / annotation.points.length,
      y: sumY / annotation.points.length,
    };
  };

  return (
    <div className="absolute inset-0 pointer-events-none">
      {/* Render existing annotations */}
      {annotations.map((annotation, annIndex) => {
        const color = getColor(annIndex);
        const center = getAnnotationCenter(annotation);
        const canEdit = !!annotation.id && !!onAnomalyClick;
        
        if (annotation.type === 'Point') {
          return annotation.points.map((point, pointIndex) => (
            <div 
              key={`ann-${annIndex}-point-${pointIndex}`} 
              className="pointer-events-auto group"
              onClick={() => canEdit && onAnomalyClick(annotation.id!)}
              style={{ cursor: canEdit ? 'pointer' : 'default' }}
            >
              {renderPoint(point, annIndex, color)}
              {/* Edit button on hover */}
              {canEdit && (
                <div
                  className="absolute opacity-0 group-hover:opacity-100 transition-opacity bg-slate-800 text-white p-1 rounded shadow-lg"
                  style={{
                    left: point.x * scale + 12,
                    top: point.y * scale - 12,
                  }}
                  title="Edit anomaly"
                >
                  <Edit2 className="w-3 h-3" />
                </div>
              )}
            </div>
          ));
        } else {
          // Polygon/Rectangle - make each vertex individually hoverable
          return (
            <div key={`ann-${annIndex}-polygon`}>
              {renderPolygon(annotation.points, color)}
              
              {/* Each vertex is independently hoverable with its own edit button */}
              {annotation.points.map((point, i) => (
                <div
                  key={`vertex-click-${i}`}
                  className="absolute pointer-events-auto group/vertex"
                  onClick={(e) => {
                    e.stopPropagation();
                    canEdit && onAnomalyClick(annotation.id!);
                  }}
                  style={{
                    left: point.x * scale - 12,
                    top: point.y * scale - 12,
                    width: 24,
                    height: 24,
                    cursor: canEdit ? 'pointer' : 'default',
                  }}
                >
                  {/* Edit button - appears only when THIS vertex is hovered */}
                  {canEdit && (
                    <div
                      className="absolute opacity-0 group-hover/vertex:opacity-100 transition-opacity bg-slate-800/90 text-white p-1 rounded shadow-lg hover:bg-slate-700"
                      style={{
                        left: 16,
                        top: -4,
                      }}
                      title="Edit anomaly"
                    >
                      <Edit2 className="w-3 h-3" />
                    </div>
                  )}
                </div>
              ))}
              
              {/* Clickable center area for convenience */}
              {canEdit && (
                <div
                  className="absolute pointer-events-auto group/center"
                  onClick={() => onAnomalyClick(annotation.id!)}
                  style={{
                    left: (Math.min(...annotation.points.map(p => p.x)) * scale) + 10,
                    top: (Math.min(...annotation.points.map(p => p.y)) * scale) + 10,
                    width: Math.max(20, (Math.max(...annotation.points.map(p => p.x)) - Math.min(...annotation.points.map(p => p.x))) * scale - 20),
                    height: Math.max(20, (Math.max(...annotation.points.map(p => p.y)) - Math.min(...annotation.points.map(p => p.y))) * scale - 20),
                    cursor: 'pointer',
                  }}
                >
                  {/* Edit button at center when hovering center area */}
                  <div
                    className="absolute opacity-0 group-hover/center:opacity-100 transition-opacity bg-slate-800/90 text-white p-1 rounded shadow-lg"
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                    title="Edit anomaly"
                  >
                    <Edit2 className="w-3 h-3" />
                  </div>
                </div>
              )}
            </div>
          );
        }
      })}

      {/* Render pending polygon annotation */}
      {annotationMode === 'polygon' && pendingPoints.length > 0 && (
        <>
          {renderPolygon(pendingPoints, '#f59e0b', true)}
          {pendingPoints.map((point, i) => (
            <div
              key={`pending-vertex-${i}`}
              className="absolute text-xs font-bold text-white px-1 rounded animate-pulse"
              style={{
                left: point.x * scale + 8,
                top: point.y * scale - 8,
                backgroundColor: '#f59e0b',
              }}
            >
              {i + 1}
            </div>
          ))}
        </>
      )}

      {/* Render pending rectangle */}
      {annotationMode === 'rectangle' && rectStartPoint && renderRectanglePreview()}

      {/* Mode indicator */}
      {annotationMode && (
        <div className="absolute top-2 left-2 bg-black/70 text-white px-3 py-1 rounded-full text-sm pointer-events-auto">
          {annotationMode === 'point' && 'Click to place point'}
          {annotationMode === 'rectangle' && (
            rectStartPoint 
              ? 'Click to set second corner' 
              : 'Click to set first corner'
          )}
          {annotationMode === 'polygon' && (
            <>
              Click to add vertices ({pendingPoints.length}/3 min)
              {pendingPoints.length >= 3 && ' - Double click to finish'}
            </>
          )}
        </div>
      )}
    </div>
  );
}
